/**
 * Utility functions for cache management in the Blink application
 */

/**
 * Clears all browser caches and storage, then reloads the page with a cache-busting parameter
 * @param reloadPage Whether to reload the page after clearing cache (default: true)
 * @returns Promise that resolves when cache clearing is complete
 */
export const clearCacheAndReload = async (reloadPage: boolean = true): Promise<void> => {
  // Clear browser caches if available
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Browser caches cleared successfully');
    } catch (e) {
      console.error('Error clearing browser caches:', e);
    }
  }
  
  // Clear localStorage and sessionStorage
  try {
    localStorage.clear();
    sessionStorage.clear();
    console.log('Local and session storage cleared successfully');
  } catch (e) {
    console.error('Error clearing storage:', e);
  }
  
  // Reload the page with cache-busting parameter if requested
  if (reloadPage) {
    const cacheBuster = `?cache=${Date.now()}`;
    window.location.href = window.location.pathname + cacheBuster;
  }
};

/**
 * Adds a cache-busting parameter to a URL
 * @param url The URL to add a cache-busting parameter to
 * @returns URL with cache-busting parameter
 */
export const addCacheBuster = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}cache=${Date.now()}`;
};

/**
 * Creates a cache-busting version of an image URL
 * @param imageUrl The image URL to add a cache-busting parameter to
 * @returns Image URL with cache-busting parameter
 */
export const getCacheBustedImageUrl = (imageUrl: string): string => {
  return addCacheBuster(imageUrl);
};

/**
 * Checks if the application is experiencing cache-related issues
 * @returns True if cache issues are detected
 */
export const detectCacheIssues = (): boolean => {
  // This is a simple implementation that could be expanded
  // based on specific indicators of cache problems in your app
  try {
    // Check for failed resources that might indicate cache issues
    const failedResources = performance
      .getEntriesByType('resource')
      .filter(resource => {
        // @ts-ignore - duration of 0 often indicates a cached resource
        return resource.duration === 0 || resource.transferSize === 0;
      });
    
    return failedResources.length > 5; // Arbitrary threshold
  } catch (e) {
    console.error('Error detecting cache issues:', e);
    return false;
  }
};
